import React from "react";
import { ArrowRight, TrendingUp } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from 'react';
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function InstitutionalMandate() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="institutional-mandate" className="dark bg-background text-foreground relative w-full overflow-hidden min-h-[85vh] flex items-center justify-center">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562991/img/global_financial_district_skyline.png)] bg-cover bg-center opacity-20 grayscale mix-blend-luminosity" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/40 to-background/90" />
        {}
        <div className="absolute inset-0 opacity-[0.03] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIi8+CjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiMwMDAiLz4KPC9zdmc+')] pointer-events-none" />
      </div>

      {}
      <div ref={ref} className={cn("relative z-10 container mx-auto px-4 py-32 flex flex-col items-center text-center transition-all duration-1000 ease-out transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
        {}
        <div className="mb-8 inline-flex items-center gap-2 border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-white/70 backdrop-blur-sm">
          <span className="h-1 w-1 rounded-full bg-white" />
          Global Capital Markets
        </div>

        {}
        <h1 className="font-serif text-5xl md:text-7xl lg:text-[84px] leading-[1.1] tracking-tight text-white mb-6 max-w-5xl mx-auto">
          Institutional Capital. <br className="hidden md:block" />
          <span className="text-white/90">Structured for Growth.</span>
        </h1>

        {}
        <p className="font-sans text-lg md:text-2xl text-[#C0C0C0] mb-12 max-w-3xl mx-auto font-light tracking-wide">Business Funding. Real Estate Capital. Private Credit.</p>

        {}
        <div className="flex flex-col sm:flex-row items-center gap-6 w-full sm:w-auto">
          <Button asChild size="lg" className="h-14 px-8 text-base font-medium bg-[#E5E5E5] text-black hover:bg-white hover:text-black rounded-none min-w-[200px] transition-all duration-300 shadow-[0_0_20px_rgba(255,255,255,0.1)]">
            <Link to="/ContactPage">
              Request Capital
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>

          <Button asChild variant="outline" size="lg" className="h-14 px-8 text-base font-medium border border-[#C0C0C0]/50 text-[#E5E5E5] hover:bg-[#E5E5E5] hover:text-black hover:border-[#E5E5E5] bg-transparent rounded-none min-w-[200px] backdrop-blur-sm transition-all duration-300">
            <Link to="/BusinessCapitalPage">
              Explore Funding Solutions
            </Link>
          </Button>
        </div>

        {}
        <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16 border-t border-white/10 pt-8 w-full max-w-4xl opacity-60">
            {[{
          label: "Capital Deployed",
          value: "$500M+"
        }, {
          label: "Active Markets",
          value: "Nationwide"
        }, {
          label: "Closing Speed",
          value: "24-48 Hours"
        }, {
          label: "Network Partners",
          value: "50+"
        }].map((stat, i) => <div key={i} data-index={i} className="flex flex-col items-center justify-center text-center">
                    <span className="font-serif text-2xl text-white mb-1">{stat.value}</span>
                    <span className="font-sans text-xs uppercase tracking-widest text-white/50">{stat.label}</span>
                </div>)}
        </div>
      </div>
    </section>;
}